import React, {useCallback, useMemo} from 'react';

import {getSearchableRegistrySupportedOligoAwareTypes} from 'constants/RegistryConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {getAllTagSchemasFromCache} from 'lookup-config/AllSchemasQueries';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {Filter, FilterKey, keyToEmptyFilter, Operator, SearchContext, SortKey} from 'search/SearchConstants';
import ItemSearchInput, {Item, ItemSearchInputProps} from 'ui-library/attach-dropdown/ItemSearchInput';
import {ChipSize} from 'ui-library/chips/Chip';
import {searchPost} from 'ui-library/inputs/ChipSearchInput';
import {getIconIdForSearchResult} from 'ui-library/inputs/ItemOptionContent';
import {OptionData} from 'ui-library/menus/MenuConstants';
import APIUtil from 'util/APIUtil';
import {ElementKeyboardEvent} from 'util/Types';

export interface BioentitySearchInputProps {
  autoFocus?: boolean;
  // Marked as optional here so this interface can fit to ItemSearchProps but there is a default prop
  // for it so we can rely on it being present in the component.
  bioentityTypes?: Array<string>;
  chipSize?: ChipSize;
  className?: string;
  query?: string;
  initialQuery?: string;
  onUpdateQuery?: (query: string) => void;
  onBlur?: () => void;
  onChange: (bioentities: Array<BioentitySummary | Item>) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  selectedItems: Array<BioentitySummary | Item>;
  tagSchemaOrInterfaceId?: string | null;
  /* If provided, restrict search to only containable schemas. Since tagSchemaOrInterfaceId is more specific,
   * it overrides this filter if both props are provided. */
  containableItemsOnly?: boolean;
  shouldAllowMultiple?: boolean;
  hideSelectedResults?: boolean;
  context?: SearchContext;
  disabled?: boolean;
  isReadOnly?: boolean;
  placeholderText?: string;
  validIds?: Array<string> | null;
  additionalFilters?: Array<Filter>;
  scrollable?: boolean;
}

/**
 * Input to search and select one or more bioentities.
 *
 * This component takes selected values of shape Item, but calls the onChange handler with values of
 * shape BioentitySummary. This is technically incorrect as the underlying ItemSearchInput doesn't update the
 * selected item values, so onChange may be called with values of shape Item.
 */
export default function BioentitySearchInput(props: BioentitySearchInputProps): JSX.Element {
  const fetchParams: ItemSearchInputProps<BioentitySummary | Item>['fetchParams'] = useMemo(() => {
    const allContainableSchemas = getAllTagSchemasFromCache().filter((schema) => schema.isContainable);
    const filters: Array<Filter> = [
      keyToEmptyFilter(FilterKey.ARCHIVE_PURPOSES),
      {
        key: FilterKey.TYPES,
        operator: Operator.IS_ONE_OF,
        value: props.bioentityTypes ?? getSearchableRegistrySupportedOligoAwareTypes(),
      },
    ];
    if (props.tagSchemaOrInterfaceId) {
      if (
        APIUtil.isSchemaInterface(props.tagSchemaOrInterfaceId) ||
        APIUtil.isStaticSchemaInterface(props.tagSchemaOrInterfaceId)
      ) {
        filters.push({
          key: FilterKey.SCHEMA_INTERFACE,
          operator: Operator.IS_ONE_OF,
          value: [props.tagSchemaOrInterfaceId],
        });
      } else {
        filters.push({
          key: FilterKey.SCHEMA,
          operator: Operator.IS_ONE_OF,
          value: [props.tagSchemaOrInterfaceId],
        });
      }
    } else if (props.containableItemsOnly && allContainableSchemas.length) {
      filters.push({
        key: FilterKey.SCHEMA,
        operator: Operator.IS_ONE_OF,
        value: allContainableSchemas.map(({id}) => id),
      });
    }
    if (props.additionalFilters) {
      filters.push(...props.additionalFilters);
    }
    return {
      context: props.context,
      filters,
      limit: 10,
      offset: 0,
      sorts: [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}],
    };
  }, [
    props.bioentityTypes,
    props.context,
    props.tagSchemaOrInterfaceId,
    props.containableItemsOnly,
    props.additionalFilters,
  ]);

  const getOptionDatasFromResponse = useCallback(
    (resp: GlobalSearchResponse): Array<OptionData> => {
      const idsToIgnore = props.selectedItems.map((item) => item.id);
      const filteredItemIds: Array<string> = resp.ids.filter(
        (itemId: string) =>
          !idsToIgnore.includes(itemId) && (!props.validIds || props.validIds.includes(itemId))
      );
      return filteredItemIds.map((itemId: string) => {
        const item = resp.fileSummariesById[itemId];
        return {
          name: item.name,
          icon: getIconIdForSearchResult({item, type: APIUtil.apiIdToType(itemId)}),
          value: {
            item,
            item_id: itemId,
            name: item.name,
            type: APIUtil.apiIdToType(itemId),
          },
        };
      });
    },
    [props.selectedItems, props.validIds]
  );

  return (
    <ItemSearchInput<BioentitySummary | Item>
      autoFocus={props.autoFocus}
      chipSize={props.chipSize}
      className={props.className}
      fetchParams={fetchParams}
      query={props.query}
      initialQuery={props.initialQuery}
      onUpdateQuery={props.onUpdateQuery}
      itemKey="data"
      onBlur={props.onBlur}
      onChange={props.onChange}
      onKeyDown={props.onKeyDown}
      placeholder={props.placeholderText ?? 'Search for entity'}
      selectedItems={props.selectedItems}
      shouldAllowMultiple={props.shouldAllowMultiple}
      hideSelectedResults={props.hideSelectedResults}
      disabled={props.disabled}
      isReadOnly={props.isReadOnly}
      searchURL="/1/api/search"
      doSearch={searchPost}
      getOptionDatasFromResponse={getOptionDatasFromResponse}
      scrollable={props.scrollable}
    />
  );
}

type BioentitySearchInputWithItemsProps = {onChange: (newItems: Array<Item>) => void} & Omit<
  BioentitySearchInputProps,
  'onChange'
>;

/**
 * Input to search and select one or more bioentities.
 *
 * Use this component when only the ids of the bioentities are available and needed. It expects the selected
 * values to be of shape Item, and calls the onChange handler with values of shape Item.
 */
export function BioentitySearchInputWithItems(props: BioentitySearchInputWithItemsProps): JSX.Element {
  return <BioentitySearchInput {...props} />;
}
